<template>
  <div>
    <div :class="{ '': !showDependency && !showMappingTable }" class="form form_justify_left pt-3">
      <label v-if="type !== 'checkbox'" class="form_label">
        {{ settingLabel }} <span v-if="settingRequired" class="text-danger">*</span>
        <hover-icon v-if="settingDescription" :title="settingDescription" hover icon="question-mark-circle" />
      </label>
      <template v-if="type === 'text'">
        <input
          :class="['form_field', inputClass]"
          :disabled="disabled"
          :required="settingRequired"
          :type="setting && setting.display_as_password && !isShowPassword ? 'password' : 'text'"
          :value="modelValue"
          @input="handleInput"
        />
        <span v-if="setting && setting.display_as_password" class="eye-viewer" @click="toggleShowPassword">
          <svg class="eye-icon">
            <use v-if="!isShowPassword" xlink:href="#eye-icon"></use>
            <use v-else xlink:href="#slashed-eye-icon"></use>
          </svg>
        </span>
      </template>
      <template v-if="isTypeADate">
        <FormDateTimePicker
          :mode="type === 'datetime' ? 'dateTime' : 'date'"
          :model-value="modelValue"
          @update:modelValue="handleInput"
        />
      </template>
      <template v-if="type === 'checkbox'">
        <input
          :id="inputId"
          :checked="inputValue"
          :class="['form_knob_check', inputClass]"
          :disabled="disabled"
          :required="settingRequired"
          :value="modelValue"
          type="checkbox"
          @input="handleInput"
        />
        <label :for="inputId" class="form_knob">
          <span>{{ settingLabel }}</span>
          <span class="px-2">
            <hover-icon v-if="settingDescription" :title="settingDescription" hover icon="question-mark-circle" />
          </span>
        </label>
      </template>

      <vue-multi-select
        v-if="type === 'select'"
        :clear-on-select="!multiple"
        :close-on-select="!multiple"
        :disabled="disabled"
        :multiple="multiple"
        :options="uniqueOptions"
        :placeholder="`Select ${settingLabel}`"
        :preserve-search="true"
        :required="settingRequired"
        :tag-placeholder="trans('flows.Press enter to add custom option')"
        :taggable="setting.can_add_option"
        v-bind:model-value="modelValue"
        @tag="handleAddOption($event)"
        @update:modelValue="value => this.$emit('update:modelValue', value)"
      />
      <slot v-if="type === null && settingDescription === null" name="field"></slot>
      <slot v-if="type === 'text'" name="extra-input"></slot>
    </div>
    <div v-if="showDependency">
      <div v-for="dependency in dependencies" :key="dependency.id">
        <form-input
          v-if="isShowDependencyInput(dependency)"
          :application1="application1"
          :application2="application2"
          :dependencies="dependency.dependency_settings"
          :dependency-values="dependencyValues"
          :help-text="dependency.description"
          :input-class="dependencyInputClass"
          :integration-id="integrationId"
          :integrationFlowId="integrationFlowId"
          :label="dependency.name"
          :model-value="getDependencyValue(dependency.id)"
          :multiple="dependency.is_multiple"
          :options="dependency.application_setting_options"
          :required="isDependencyRequired(dependency)"
          :setting="dependency"
          :type="dependency.field_type_name"
          @update:modelValue="handleInputDependency(dependency.id, $event)"
        />
      </div>
    </div>
    <div v-if="showMappingTable">
      <div class="my-11 w-full pr-8">
        <mapping-table
          :id="mappingTableId"
          :application1="application1"
          :application2="application2"
          :header1="setting.tableHeader.header_1 || 'Header'"
          :header2="setting.tableHeader.header_2 || 'Header'"
          :is-user-integration="isIntegration"
          :is-user-integration-flow="isIntegrationFlow"
          :mapping-tables="setting.mappingTable"
          :setting-id="setting.id"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiSelect from 'vue-multiselect';
import FormDateTimePicker from '@/components/Shared/Form/DateTimePicker/Index.vue';
import MappingTable from './MappingTable';
import HoverIcon from '@/components/Shared/HoverIcon.vue';

export default {
  name: 'FormInput',

  components: {
    HoverIcon,
    VueMultiSelect,
    MappingTable,
    FormDateTimePicker,
  },

  emits: ['update:modelValue', 'input-dependency'],

  props: {
    label: {
      type: String,
      required: true,
    },

    type: {
      type: String,
      default: null,
    },

    helpText: {
      type: String,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: [String, Boolean, Array, Object],
      default: null,
    },

    options: {
      type: Array,
      default: () => [],
    },

    required: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    dependencies: {
      type: Array,
      default: null,
    },

    setting: {
      type: Object,
      default: null,
    },

    uniqueSuffix: {
      type: [String, Number],
      default: null,
    },

    integrationFlowId: {
      type: [Number, String],
      default: null,
    },

    integrationId: {
      type: [Number, String],
      default: null,
    },

    application1: {
      type: Object,
      default: null,
    },

    application2: {
      type: Object,
      default: null,
    },

    dependencyValues: {
      type: Object,
      default: null,
    },

    inputClass: {
      type: String,
      default: null,
    },

    dependencyInputClass: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      inputValue: null,
      isShowPassword: false,
    };
  },

  beforeMount() {
    if (this.type === 'checkbox' && typeof this.modelValue === 'string') {
      this.inputValue = this.modelValue === '1';
    } else {
      this.inputValue = this.modelValue;
    }
  },

  computed: {
    classInput() {
      if (this.type === 'text') {
        return 'w-full form-control form-input form-input-bordered';
      } else if (this.type === 'checkbox') {
        return 'checkbox mt-2';
      } else if (this.type === 'select' && !this.multiple) {
        return 'w-full form-control form-select';
      }
    },

    showDependency() {
      return Array.isArray(this.dependencies) && this.dependencies.length > 0 && this.type === 'checkbox';
    },

    showMappingTable() {
      return (
        this.setting && this.setting.is_require_mapping_table && this.inputValue === true && this.type === 'checkbox'
      );
    },

    inputId() {
      return `${this.label.toLowerCase().replaceAll(' ', '-').trim()}${this.label.length}-${this.uniqueSuffix}`;
    },

    isIntegration() {
      return this.integrationId !== null;
    },

    isIntegrationFlow() {
      return this.integrationFlowId !== null;
    },

    mappingTableId() {
      return this.isIntegration ? this.integrationId : this.integrationFlowId;
    },

    settingLabel() {
      return this.setting?.pivot?.override_name || this.label;
    },

    settingDescription() {
      return this.setting?.pivot?.override_description || this.helpText;
    },

    settingRequired() {
      const overrideRequired = this.setting?.pivot?.override_is_required;

      return overrideRequired == null ? this.required : overrideRequired;
    },

    uniqueOptions() {
      const mergedOptions = JSON.parse(JSON.stringify(this.options));
      if (this.multiple && Array.isArray(this.modelValue)) {
        mergedOptions.push(...this.modelValue);
      } else if (!this.multiple && this.modelValue !== '') {
        mergedOptions.push(this.modelValue);
      }

      return _.filter(_.uniq(mergedOptions), option => {
        return option != '' && !Array.isArray(option);
      });
    },
    isTypeADate() {
      return ['date', 'datetime'].includes(this.type);
    },
  },

  methods: {
    handleInput(event) {
      if (this.type === 'checkbox') {
        this.inputValue = event.target.checked;
      } else if (this.isTypeADate) {
        this.inputValue = event;
      } else {
        this.inputValue = event.target.value;
      }

      this.$emit('update:modelValue', this.inputValue);
    },

    handleInputDependency(dependencyId, value) {
      this.dependencyValues[dependencyId] = {
        value: value,
        setting_id: dependencyId,
      };

      this.$emit('input-dependency', { dependencyId, value });
    },

    getDependencyValue(dependencyId) {
      if (this.dependencyValues != null && !this.dependencyValues.hasOwnProperty(dependencyId)) {
        this.dependencyValues[dependencyId] = {
          value: null,
          setting_id: dependencyId,
        };
      }

      return this.dependencyValues[dependencyId].value;
    },

    toggleShowPassword() {
      this.isShowPassword = !this.isShowPassword;
    },

    isShowDependencyInput(dependency) {
      return this.inputValue || dependency.is_require_mapping_table;
    },

    handleAddOption(newField) {
      this.options.push(newField);

      if (this.multiple) {
        this.modelValue.push(newField);
      } else {
        this.$emit('input', newField);
      }
    },

    isDependencyRequired(dependency) {
      return dependency?.pivot?.override_is_required == null
        ? dependency.is_required
        : dependency?.pivot?.override_is_required;
    },
  },
};
</script>

<style>
.eye-viewer {
  float: right;
  margin-top: -38px;
  margin-right: 5px;
  position: relative;
  z-index: 10;
  cursor: pointer;
}

.eye-icon {
  width: 1.5rem;
  height: 1.5rem;
}
</style>
